import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Users, Pencil, Power, ArrowLeft, Shield } from "lucide-react";
import { toast } from "sonner";

export default function AdminTeams() {
  const { user, loadMemberships } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {mode:'create'|'edit', team?}
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && user.role !== "club_admin") {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clubs/me/teams", { params: { include_inactive: true } });
      setTeams(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setName(""); setDialog({ mode: "create" }); };
  const openEdit = (t) => { setName(t.name); setDialog({ mode: "edit", team: t }); };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Bitte Name angeben"); return; }
    setSaving(true);
    try {
      if (dialog.mode === "create") {
        await api.post("/clubs/me/teams", { name: trimmed });
        toast.success(`Mannschaft "${trimmed}" angelegt`);
      } else {
        await api.put(`/teams/${dialog.team.id}`, { name: trimmed });
        toast.success("Mannschaft umbenannt");
      }
      setDialog(null);
      await load();
      await loadMemberships();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const toggleActive = async (team) => {
    const action = team.active ? "deaktivieren" : "aktivieren";
    if (!window.confirm(`Mannschaft "${team.name}" ${action}? Daten bleiben erhalten.`)) return;
    try {
      await api.put(`/teams/${team.id}`, { active: !team.active });
      toast.success(`Mannschaft ${team.active ? "deaktiviert" : "aktiviert"}`);
      await load();
      await loadMemberships();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Aktualisieren fehlgeschlagen");
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 max-w-4xl mx-auto" data-testid="admin-teams-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}
            className="text-zinc-400 hover:text-brand-lighter hover:bg-zinc-900" data-testid="at-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />
          </Button>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-brand-lighter">Verwaltung</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold uppercase">Mannschaften</h1>
          </div>
        </div>
        <Button onClick={openCreate} className="bg-brand hover:bg-brand-light text-white rounded-md" data-testid="at-create-btn">
          <Plus className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Mannschaft anlegen</span>
        </Button>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Lädt…</div>
      ) : teams.length === 0 ? (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-8 text-center text-zinc-500 text-sm">
          Noch keine Mannschaften. Klicke &bdquo;Mannschaft anlegen&ldquo;.
        </div>
      ) : (
        <div className="space-y-2" data-testid="at-list">
          {teams.map((t) => (
            <div key={t.id}
              className={"rounded-md border bg-zinc-900/40 p-4 flex items-center justify-between gap-3 " + (t.active ? "border-zinc-800" : "border-zinc-800/50 opacity-60")}
              data-testid={`at-team-${t.id}`}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-brand/20 flex items-center justify-center rounded-md">
                  <Users className="h-5 w-5 text-brand-lighter" />
                </div>
                <div>
                  <div className="font-semibold text-zinc-100">{t.name}</div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500">
                    {t.active ? "Aktiv" : "Deaktiviert"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => openEdit(t)}
                  className="text-zinc-400 hover:text-brand-lighter hover:bg-zinc-800" data-testid={`at-edit-${t.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => toggleActive(t)}
                  className={"hover:bg-zinc-800 " + (t.active ? "text-zinc-400 hover:text-amber-400" : "text-emerald-400 hover:text-emerald-300")}
                  data-testid={`at-toggle-${t.id}`}>
                  <Power className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "create" ? "Mannschaft anlegen" : "Mannschaft bearbeiten"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-zinc-400">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                placeholder="z. B. U15"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-10 rounded-md"
                data-testid="at-name-input" autoFocus
                onKeyDown={(e) => e.key === "Enter" && save()} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}
              className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-900 rounded-md">Abbrechen</Button>
            <Button onClick={save} disabled={saving}
              className="bg-brand hover:bg-brand-light text-white rounded-md" data-testid="at-save-btn">
              {saving ? "Speichern…" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
